import { useCallback, useEffect, useState } from "react";
import { adminAlerts, adminCreateAlert, adminUpdateAlert, adminDeleteAlert, adminAlertReport, adminBills } from "@/lib/api";
import { positionBadge } from "@/lib/bills";
import { Trash2, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import ResourceList from "@/components/admin/ResourceList";
import ResourceDrawer from "@/components/admin/ResourceDrawer";

const POSITIONS = ["Support", "Oppose", "Monitor"];
const STATUS = ["Active", "Archived"];
const EMPTY = {
  slug: "", title: "", bill_id: "", issue_area: "sanctity-of-life", position: "Support",
  status: "Active", summary: "", talking_points: [""], default_subject: "", default_message: "",
};

function AlertRow({ a, onEdit, onDelete, onReport }) {
  return (
    <div className="bg-white border border-stone-200 p-5 flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${positionBadge(a.position)}`}>{a.position}</span>
          <span className="text-xs text-stone-500">{a.status}</span>
        </div>
        <div className="font-serif text-xl text-ccc-plum">{a.title}</div>
        <div className="text-sm text-stone-600 mt-1 line-clamp-2">{a.summary}</div>
      </div>
      <div className="flex gap-2 shrink-0">
        <button onClick={() => onReport(a)} className="text-ccc-purple text-sm inline-flex items-center gap-1"><BarChart3 size={14}/> Report</button>
        <button onClick={() => onEdit(a)} className="text-ccc-purple text-sm underline">Edit</button>
        <button onClick={() => onDelete(a.id)} className="text-red-600" aria-label="Delete"><Trash2 size={14}/></button>
      </div>
    </div>
  );
}

function AlertForm({ editing, setEditing, bills }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <input placeholder="Slug" value={editing.slug} onChange={e => setEditing({...editing, slug: e.target.value})} className="px-3 py-2 border border-stone-300"/>
      <select value={editing.status} onChange={e => setEditing({...editing, status: e.target.value})} className="px-3 py-2 border border-stone-300">
        {STATUS.map(s => <option key={s}>{s}</option>)}
      </select>
      <input placeholder="Title" value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300"/>
      <select value={editing.position} onChange={e => setEditing({...editing, position: e.target.value})} className="px-3 py-2 border border-stone-300">
        {POSITIONS.map(p => <option key={p}>{p}</option>)}
      </select>
      <select value={editing.bill_id || ""} onChange={e => setEditing({...editing, bill_id: e.target.value || null})} className="px-3 py-2 border border-stone-300">
        <option value="">— No related bill —</option>
        {bills.map(b => <option key={b.id} value={b.id}>{b.number} — {b.title}</option>)}
      </select>
      <textarea placeholder="Summary" rows={3} value={editing.summary} onChange={e => setEditing({...editing, summary: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300"/>
      <div className="col-span-2">
        <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">Talking points</div>
        {editing.talking_points.map((tp, i) => (
          <div key={tp.id} className="flex gap-2 mb-2">
            <input value={tp.text}
              onChange={e => {
                const arr = editing.talking_points.map(x => x.id === tp.id ? { ...x, text: e.target.value } : x);
                setEditing({ ...editing, talking_points: arr });
              }}
              placeholder={`Point ${i+1}`}
              className="flex-1 px-3 py-2 border border-stone-300"/>
            <button type="button"
              onClick={() => setEditing({ ...editing, talking_points: editing.talking_points.filter(x => x.id !== tp.id) })}
              className="text-red-600 px-2" aria-label="Remove point">
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
        <button type="button"
          onClick={() => setEditing({ ...editing, talking_points: [...editing.talking_points, { id: crypto.randomUUID(), text: "" }] })}
          className="text-ccc-purple text-sm">+ Add point</button>
      </div>
      <input placeholder="Default subject" value={editing.default_subject} onChange={e => setEditing({...editing, default_subject: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300"/>
      <textarea placeholder="Default message" rows={8} value={editing.default_message} onChange={e => setEditing({...editing, default_message: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300 font-serif"/>
    </div>
  );
}

function AlertReport({ report }) {
  return (
    <div data-testid="campaign-report">
      <div className="text-6xl font-serif text-ccc-plum mb-1">{report.total_actions}</div>
      <div className="text-sm text-stone-600 mb-6">Actions taken</div>
      <div className="text-xs uppercase tracking-wider text-ccc-gold-dark mb-2">By district</div>
      <div className="space-y-1 mb-6">
        {report.by_district.map(d => (
          <div key={d.district} className="flex justify-between text-sm border-b border-stone-100 py-1">
            <span>{d.district}</span><span className="text-ccc-purple font-semibold">{d.count}</span>
          </div>
        ))}
        {report.by_district.length === 0 && <div className="text-sm text-stone-500">No actions yet.</div>}
      </div>
      <div className="text-xs uppercase tracking-wider text-ccc-gold-dark mb-2">Top parishes</div>
      <div className="space-y-1">
        {report.by_parish.map(p => (
          <div key={p.parish} className="flex justify-between text-sm border-b border-stone-100 py-1">
            <span>{p.parish}</span><span className="text-ccc-purple">{p.count}</span>
          </div>
        ))}
        {report.by_parish.length === 0 && <div className="text-sm text-stone-500">—</div>}
      </div>
    </div>
  );
}

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [bills, setBills] = useState([]);
  const [editing, setEditing] = useState(null);
  const [report, setReport] = useState(null);

  const load = useCallback(() => adminAlerts().then(setItems), []);
  useEffect(() => { load(); adminBills().then(setBills); }, [load]);

  const startEdit = (source) => {
    const tp = (source.talking_points || []).map(t =>
      typeof t === "string" ? { id: crypto.randomUUID(), text: t } : t,
    );
    if (tp.length === 0) tp.push({ id: crypto.randomUUID(), text: "" });
    setEditing({ ...source, talking_points: tp });
  };

  const save = async () => {
    try {
      const payload = { ...editing, talking_points: editing.talking_points.map(t => t.text).filter(x => x.trim()) };
      if (items.find(i => i.id === editing.id)) { await adminUpdateAlert(editing.id, payload); toast.success("Updated"); }
      else { await adminCreateAlert(payload); toast.success("Created"); }
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await adminDeleteAlert(id); load();
  };
  const openReport = async (a) => setReport({ alert: a, ...(await adminAlertReport(a.id)) });

  return (
    <ResourceList
      eyebrow="Campaigns"
      title="Action Alerts."
      testId="admin-campaigns"
      newLabel="New Alert"
      newButtonTestId="new-campaign"
      onNew={() => startEdit({ ...EMPTY })}
    >
      <div className="grid gap-3">
        {items.map(a => <AlertRow key={a.id} a={a} onEdit={startEdit} onDelete={remove} onReport={openReport}/>)}
      </div>

      <ResourceDrawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit action alert" : "New action alert"}
        size="lg"
        onSave={save}
      >
        {editing && <AlertForm editing={editing} setEditing={setEditing} bills={bills}/>}
      </ResourceDrawer>

      <ResourceDrawer
        open={!!report}
        onClose={() => setReport(null)}
        subtitle="Report"
        title={report?.alert.title}
        size="md"
      >
        {report && <AlertReport report={report}/>}
      </ResourceDrawer>
    </ResourceList>
  );
}
